import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ScoreCircle, ScoreBars } from '../../components/MatchBar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Heart, X, MessageCircle, Phone, ArrowLeft,
  AlertTriangle, CheckCircle, Loader2, RefreshCw, Filter
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001';

// ── Modal de aviso para score < 70% ─────────────────────────────
function ModalBaixoScore({ candidato, onConfirmar, onCancelar }) {
  if (!candidato) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-yellow-600" />
          </div>
          <div>
            <p className="font-sora font-bold text-acre-dark">Compatibilidade Baixa</p>
            <p className="text-xs text-gray-400">Score abaixo de 70%</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-2">
          O perfil de <strong>{candidato.nome?.split(' ')[0]}</strong> apresenta{' '}
          <span className="text-yellow-600 font-bold">{Math.round(candidato.score_total)}% de compatibilidade</span>{' '}
          com esta vaga.
        </p>
        <p className="text-sm text-gray-500 mb-5">
          Recomendamos candidatos com score ≥ 70% para melhores resultados. Deseja continuar mesmo assim?
        </p>

        <div className="flex gap-3">
          <button onClick={onCancelar}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={onConfirmar}
            className="flex-1 px-4 py-2.5 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-bold transition-colors">
            Selecionar assim mesmo
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MatchEmpresa() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [resultado,  setResultado]  = useState(null);
  const [curtidas,   setCurtidas]   = useState({});
  const [loading,    setLoading]    = useState(true);
  const [minScore,   setMinScore]   = useState(0);
  const [filtro,     setFiltro]     = useState('todos');
  const [enviando,   setEnviando]   = useState(null);
  const [modalBaixo, setModalBaixo] = useState(null); // candidato obj

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [rMatches, rCurtidas] = await Promise.all([
        api.get(`/vagas/${id}/matches?minScore=0&limit=100`),
        api.get(`/vagas/${id}/match/curtidas`).catch(() => ({ data: {} })),
      ]);
      setResultado(rMatches.data);
      setCurtidas(rCurtidas.data || {});
    } catch {
      toast.error('Erro ao carregar candidatos.');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Like / Pular candidato ───────────────────────────────────
  const curtirCandidato = async (candidatoId, acao) => {
    if (enviando) return;
    setEnviando(candidatoId + acao);
    try {
      await api.post(`/vagas/${id}/match/candidatos/${candidatoId}/${acao === 'curtiu' ? 'curtir' : 'pular'}`);
      setCurtidas(p => ({ ...p, [candidatoId]: acao }));
      if (acao === 'curtiu') toast.success('💚 Candidato curtido!');
    } catch {
      toast.error('Erro ao registrar.');
    }
    setEnviando(null);
  };

  // ── Selecionar (move para Em Seleção) ───────────────────────
  const tentarSelecionar = (m) => {
    if (m.score_total < 70) {
      setModalBaixo(m);
    } else {
      confirmarSelecao(m);
    }
  };

  const confirmarSelecao = async (m) => {
    setModalBaixo(null);
    try {
      await api.post('/selecao', { candidato_id: m.user_id, vaga_id: id, status: 'em_selecao' });
      toast.success(`✅ ${m.nome?.split(' ')[0]} movido para Em Seleção!`);
    } catch { toast.error('Erro ao selecionar.'); }
  };

  // ── Iniciar chat ─────────────────────────────────────────────
  const iniciarChat = async (candidatoId, nome) => {
    setEnviando('chat' + candidatoId);
    try {
      await api.post('/chat/conversas', { participante_id: candidatoId });
      await api.post('/selecao', { candidato_id: candidatoId, vaga_id: id, status: 'em_selecao' });
      toast.success(`💬 Conversa iniciada com ${nome?.split(' ')[0]}!`);
      navigate('/chat');
    } catch { toast.error('Erro ao iniciar conversa.'); }
    setEnviando(null);
  };

  // ── WhatsApp ─────────────────────────────────────────────────
  const abrirWhatsApp = (numero, nome, curriculoId) => {
    api.post(`/vagas/${id}/matches/${curriculoId}/contato`).catch(() => {});
    const msg = encodeURIComponent(`Olá ${nome?.split(' ')[0]}! Vi seu perfil no AcreJob e tenho interesse em conversar sobre uma oportunidade.`);
    window.open(`https://wa.me/55${numero.replace(/\D/g,'')}?text=${msg}`, '_blank');
  };

  // ── Filtragem ────────────────────────────────────────────────
  const todosMatches = resultado?.matches || [];
  const visiveis = todosMatches
    .filter(m => m.score_total >= minScore)
    .filter(m => {
      if (filtro === 'curtidos') return curtidas[m.user_id] === 'curtiu';
      if (filtro === 'pendentes') return !curtidas[m.user_id];
      return true;
    });

  const totalCurtidos = Object.values(curtidas).filter(a => a === 'curtiu').length;

  return (
    <Layout
      title={resultado?.vaga?.titulo || 'Candidatos Compatíveis'}
      subtitle={`${resultado?.vaga?.cidade_vaga || ''} · ${resultado?.total_matches || 0} candidatos`}>

      <ModalBaixoScore
        candidato={modalBaixo}
        onConfirmar={() => confirmarSelecao(modalBaixo)}
        onCancelar={() => setModalBaixo(null)}
      />

      {/* Botão Voltar */}
      <button onClick={() => navigate('/empresa')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-acre-green mb-4 transition-colors">
        <ArrowLeft size={15} /> Voltar ao painel
      </button>

      {/* Banner algoritmo */}
      <div className="card p-4 mb-5 bg-gradient-to-r from-acre-dark to-acre-green text-white">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🧠</span>
          <div>
            <p className="font-sora font-bold text-sm">Algoritmo de Match AcreJob</p>
            <p className="text-white/70 text-xs mt-0.5">
              Área de interesse <strong className="text-white">60%</strong> +
              Proximidade geográfica <strong className="text-white">40%</strong> ·
              Chat liberado para score <strong className="text-white">≥ 80%</strong>
            </p>
          </div>
          {totalCurtidos > 0 && (
            <div className="ml-auto text-right flex-shrink-0">
              <p className="font-sora font-bold text-acre-gold text-lg">{totalCurtidos}</p>
              <p className="text-white/60 text-xs">curtido(s)</p>
            </div>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        {[
          { v: 'todos',     l: 'Todos' },
          { v: 'pendentes', l: '⏳ Pendentes' },
          { v: 'curtidos',  l: '💚 Curtidos' },
        ].map(f => (
          <button key={f.v} onClick={() => setFiltro(f.v)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              filtro === f.v
                ? 'bg-acre-green text-white border-acre-green'
                : 'bg-white text-gray-500 border-gray-200 hover:border-acre-green hover:text-acre-green'
            }`}>
            {f.l}
          </button>
        ))}

        <div className="flex items-center gap-1 ml-auto">
          <Filter size={12} className="text-gray-400" />
          {[0, 40, 60, 80].map(s => (
            <button key={s} onClick={() => setMinScore(s)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                minScore === s
                  ? 'bg-acre-green text-white border-acre-green'
                  : 'bg-white text-gray-400 border-gray-200 hover:border-acre-green'
              }`}>
              {s === 0 ? 'Todos' : `≥${s}%`}
            </button>
          ))}
        </div>

        <button onClick={carregar} title="Atualizar"
          className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-acre-green hover:border-acre-green transition-colors">
          <RefreshCw size={13} />
        </button>

        <button onClick={() => navigate('/empresa/selecao')}
          className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all">
          📋 Em Seleção →
        </button>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 size={32} className="animate-spin text-acre-green" />
          <p className="text-sm">Calculando compatibilidade...</p>
        </div>
      ) : visiveis.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-5xl mb-3">🔍</p>
          <p className="text-gray-600 font-semibold mb-1">
            {filtro === 'curtidos' ? 'Nenhum candidato curtido ainda.'
             : filtro === 'pendentes' ? 'Todos os candidatos já foram avaliados!'
             : todosMatches.length === 0 ? 'Nenhum candidato compatível encontrado.'
             : `Nenhum candidato com score ≥ ${minScore}%.`}
          </p>
          <p className="text-gray-400 text-sm">Tente reduzir o filtro de compatibilidade mínima.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visiveis.map((m, i) => {
            const foto = m.fotos?.[0] ? `${API_BASE}${m.fotos[0]}` : null;
            const acao = curtidas[m.user_id];
            const jaCurtiu = acao === 'curtiu';
            const jaPulou  = acao === 'pulou';
            const enviandoCurtir = enviando === m.user_id + 'curtiu';
            const enviandoPular  = enviando === m.user_id + 'pulou';
            const enviandoChat   = enviando === 'chat' + m.user_id;
            const baixoScore = m.score_total < 70;

            return (
              <div key={m.curriculo_id}
                className={`card p-4 flex items-center gap-3 transition-all ${
                  jaPulou  ? 'opacity-40 hover:opacity-70' :
                  jaCurtiu ? 'ring-2 ring-green-300 bg-green-50/30' :
                  'hover:shadow-md'
                }`}>

                {i < 3 && filtro === 'todos' && minScore === 0 && (
                  <span className="text-lg flex-shrink-0">{['🥇','🥈','🥉'][i]}</span>
                )}

                {/* Foto */}
                <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border-2 border-gray-100">
                  {foto
                    ? <img src={foto} alt={m.nome} className="w-full h-full object-cover"/>
                    : <div className="w-full h-full bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center text-white font-bold text-lg">
                        {m.nome?.charAt(0)}
                      </div>}
                </div>

                {/* Score */}
                <ScoreCircle score={m.score_total} size="md" />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sora font-semibold text-acre-dark text-sm">{m.nome}</span>
                    {m.selo_verificado && <span className="badge-gold text-xs">⭐ Ouro</span>}
                    {baixoScore && (
                      <span className="flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-yellow-50 text-yellow-600 border border-yellow-200">
                        <AlertTriangle size={10}/> Baixo match
                      </span>
                    )}
                    {jaCurtiu && (
                      <span className="flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
                        <CheckCircle size={10}/> Curtido
                      </span>
                    )}
                    {m.video_pitch_url && (
                      <a href={m.video_pitch_url} target="_blank" rel="noreferrer"
                        className="badge bg-purple-50 text-purple-700 text-xs hover:bg-purple-100">▶ Pitch</a>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{m.cidade} · {m.experiencia_anos || 0} anos exp.</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(m.areas_interesse || []).slice(0, 3).map(a => (
                      <span key={a} className="badge-green text-xs">{a}</span>
                    ))}
                    <span className="badge-gray text-xs capitalize">{m.disponibilidade?.replace('_',' ') || '—'}</span>
                  </div>
                  {/* Barra de progresso */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${
                        m.score_total >= 80 ? 'bg-green-500' :
                        m.score_total >= 60 ? 'bg-lime-500' :
                        m.score_total >= 40 ? 'bg-yellow-500' : 'bg-gray-300'
                      }`} style={{ width: `${m.score_total}%` }} />
                    </div>
                    <span className="text-xs font-bold text-gray-400 flex-shrink-0">{Math.round(m.score_total)}%</span>
                  </div>
                </div>

                {/* Score Bars (desktop) */}
                <div className="hidden lg:block flex-shrink-0">
                  <ScoreBars scoreArea={m.score_area} scoreGeo={m.score_geo} />
                </div>

                {/* Botões de ação */}
                <div className="flex flex-col gap-1.5 flex-shrink-0">

                  {/* Like / Skip */}
                  <div className="flex gap-1.5 justify-end">
                    {jaCurtiu ? (
                      <div className="flex items-center gap-1 text-xs text-green-600 font-bold bg-green-50 border border-green-200 px-2 py-1.5 rounded-xl">
                        <CheckCircle size={12}/> <span className="hidden sm:inline">Curtido</span>
                      </div>
                    ) : jaPulou ? (
                      <div className="flex items-center gap-1 text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2 py-1.5 rounded-xl">
                        <X size={12}/> <span className="hidden sm:inline">Pulou</span>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => curtirCandidato(m.user_id, 'pulou')} disabled={!!enviando}
                          title="Pular"
                          className="w-8 h-8 rounded-full border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 flex items-center justify-center text-gray-300 hover:text-red-500 transition-all active:scale-90 disabled:opacity-40">
                          {enviandoPular ? <Loader2 size={12} className="animate-spin" /> : <X size={14} />}
                        </button>
                        <button onClick={() => curtirCandidato(m.user_id, 'curtiu')} disabled={!!enviando}
                          title="Curtir candidato"
                          className="w-8 h-8 rounded-full border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 flex items-center justify-center text-gray-300 hover:text-green-500 transition-all active:scale-90 disabled:opacity-40">
                          {enviandoCurtir ? <Loader2 size={12} className="animate-spin text-green-500" /> : <Heart size={14} />}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Chat (score >= 80) ou Selecionar */}
                  {m.score_total >= 80 ? (
                    <button onClick={() => iniciarChat(m.user_id, m.nome)} disabled={!!enviando}
                      className="flex items-center gap-1.5 bg-acre-green hover:bg-acre-mid text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-all active:scale-95 disabled:opacity-60 whitespace-nowrap">
                      {enviandoChat ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
                      <span className="hidden sm:inline">Mensagem</span>
                    </button>
                  ) : (
                    <button onClick={() => tentarSelecionar(m)}
                      className={`flex items-center gap-1.5 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-all active:scale-95 whitespace-nowrap ${
                        baixoScore ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-blue-500 hover:bg-blue-600'
                      }`}>
                      📋 <span className="hidden sm:inline">Selecionar</span>
                    </button>
                  )}

                  {/* WhatsApp */}
                  {m.whatsapp && (
                    <button onClick={() => abrirWhatsApp(m.whatsapp, m.nome, m.curriculo_id)}
                      className="flex items-center gap-1.5 bg-[#25d366] hover:bg-[#1da851] text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-all active:scale-95 whitespace-nowrap">
                      <Phone size={12} />
                      <span className="hidden sm:inline">WhatsApp</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visiveis.length > 0 && (
        <p className="text-center text-xs text-gray-400 mt-6">
          {visiveis.length} candidato{visiveis.length !== 1 ? 's' : ''} ·{' '}
          <button onClick={() => { setFiltro('todos'); setMinScore(0); }}
            className="text-acre-green hover:underline">ver todos</button>
        </p>
      )}
    </Layout>
  );
}
